/**
 * Anmelden und Registrieren.
 *
 * Eine Ansicht für beides: Der Unterschied sind zwei Felder, und wer sich gerade
 * vertan hat, wechselt ohne die Eingaben zu verlieren.
 *
 * Der Aufbau kommt aus `templates/auth-web/AuthWeb.dc.html` — Markenfläche
 * links, Formular rechts; den Rahmen stellt `components/auth-layout.tsx`.
 * Gestaltet mit den Komponenten aus `@hausmeter/ui` — dem Design-System. Die
 * Anrede ist das informelle **du**, klein geschrieben, wie im ganzen Produkt.
 *
 * **Die Registrierung läuft seit HM-060 in zwei Schritten** (ADR 0024): Angaben,
 * dann der sechsstellige Sicherheitscode aus der E-Mail. **Das Konto entsteht
 * erst mit dem zweiten** — vorher gibt es nichts, was jemand wieder wegräumen
 * müsste. Was die Oberfläche dabei tragen muss:
 *
 * **Der Zwischenstand lebt im Browser und hat keine eigene Adresse.** Deshalb
 * ist der Weg zurück ein Knopf und kein Link: Ein Neuladen führt auf das
 * Formular zurück, und das ist ehrlicher als eine Adresse, die danach leer wäre.
 * Wer bis dahin gekommen ist, hat den Code trotzdem — er beginnt einfach neu.
 *
 * **Und die Oberfläche behauptet nicht, was der Server verschweigt.** Der
 * Endpunkt antwortet gleich, ob es zu der Adresse schon ein Konto gibt; deshalb
 * steht im zweiten Schritt, dass an eine bereits vergebene Adresse **statt des
 * Codes** ein Hinweis geht. Ohne diesen Satz stünde jemand vor sechs Kästchen
 * und wartete auf einen Code, der nie kommt — dieselbe Überlegung wie beim
 * „wenn es zu dieser Adresse ein Konto gibt" nach dem Zurücksetzen (HM-026).
 *
 * **Übernommen ist der Aufbau, nicht jede Zusage.** Der Entwurf verlangt zehn
 * Zeichen für das Passwort, der Vertrag zwölf (`PasswordSchema`); er zeigt eine
 * **Stärkeanzeige**, die eine Bewertung voraussetzt, die dieses Produkt nicht
 * hat (Regel 9); und er zeigt neben dem Countdown einen Versuchszähler
 * („Versuche: 1 von 5"). Den gibt es hier nicht: Der Server nennt die
 * verbleibenden Versuche bewusst nicht, weil eine Antwort, die sie enthält,
 * zugleich verrät, dass es diesen Vorgang gibt — und damit, dass die Adresse
 * frei war. Was gilt, steht stattdessen auf der Markenfläche.
 */
import { Alert, Button, Checkbox, Input } from "@hausmeter/ui";
import { useEffect, useId, useMemo, useState, type FormEvent } from "react";
import {
  AuthBack,
  AuthHeading,
  AuthLayout,
  AuthLink,
  AuthNote,
  AuthSteps,
  AuthSwitch,
} from "../components/auth-layout.js";
import { CodeField } from "../components/code-field.js";
import { PasswordField } from "../components/password-field.js";
import { SESSION_HOURS, SESSION_REMEMBER_DAYS, TERMS_VERSION } from "../lib/legal.js";
import {
  fieldErrorsFromDetails,
  PASSWORD_MIN_LENGTH,
  registrationErrors,
  type FieldErrors,
  type RegistrationField,
} from "../lib/registration-fields.js";
import { resendDeadline } from "../lib/resend-cooldown.js";
import {
  confirmRegistration,
  login,
  RequestError,
  resendRegistrationCode,
  startRegistration,
} from "../lib/session.js";

/** Was der Server über den versendeten Code gesagt hat. */
interface CodeStatus {
  readonly expiresAt: string;
  readonly resendAvailableAt: string;
  readonly codeLength: number;
}

export function LoginPage() {
  const [mode, setMode] = useState<"login" | "register">("login");
  /**
   * Die Stufe der Registrierung. `null` heißt „noch beim Formular"; sobald ein
   * Code unterwegs ist, steht hier, was der Server dazu gesagt hat.
   */
  const [sent, setSent] = useState<CodeStatus | null>(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  /**
   * **Vor- und Nachname statt „Name des Accounts".** Die alte Frage war eine
   * nach einem Begriff dieses Systems; die einzige ehrliche Antwort darauf
   * lautete „mein Haushalt". Der Account heißt seither nach seinem Besitzer.
   */
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [code, setCode] = useState("");
  /**
   * Vorbelegt mit `false`. Ein voreingestellter Haken nimmt die Entscheidung ab,
   * die er eigentlich abfragt — und wer an einem fremden Rechner sitzt, hat sie
   * dann schon getroffen, ohne es zu merken.
   */
  const [rememberMe, setRememberMe] = useState(false);
  /**
   * **Die eigentliche Korrektur aus HM-059.** Bisher ging
   * `acceptedTermsVersion` bei jeder Registrierung mit, ohne dass jemand gefragt
   * worden wäre — eine festgehaltene Zustimmung, die nie erteilt wurde.
   */
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /**
   * **Ob schon einmal abgeschickt wurde.** Erst danach steht ein Fehler rot am
   * Feld — wer das Formular gerade erst ausfüllt, soll nicht vor lauter
   * „Bitte gib … an" stehen. Danach aber sofort und bei jedem Tastendruck
   * neu: Die Meldung verschwindet in dem Augenblick, in dem die Angabe stimmt.
   */
  const [attempted, setAttempted] = useState(false);
  /**
   * Was der Server abgelehnt hat, obwohl die Prüfung hier es durchließ. Es gilt
   * nur, bis das Feld geändert wird — danach ist es eine Aussage über einen
   * Wert, der nicht mehr dasteht.
   */
  const [serverErrors, setServerErrors] = useState<FieldErrors>({});
  const idPrefix = useId();
  const fieldId = (field: RegistrationField) => `${idPrefix}-${field}`;

  const isRegister = mode === "register";
  const atCodeStep = isRegister && sent !== null;

  const clientErrors = registrationErrors({ firstName, lastName, email, password, termsAccepted });

  /** Der Fehler, der an einem Feld der Registrierung steht — oder keiner. */
  function errorFor(field: RegistrationField): string | undefined {
    if (!isRegister || atCodeStep) return undefined;
    return (attempted ? clientErrors[field] : undefined) ?? serverErrors[field];
  }

  /** Ein geändertes Feld nimmt die Ablehnung des Servers mit. */
  function edited(field: RegistrationField): void {
    if (serverErrors[field] === undefined) return;
    setServerErrors(({ [field]: _gone, ...rest }) => rest);
  }

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();

    if (isRegister && sent === null) {
      /*
        **Geprüft wird vor dem Absenden, und der Blick geht zum ersten Fehler.**
        Bis dahin sperrte der fehlende Haken den Knopf ohne Begründung, und ein
        zu kurzes Passwort meldete nur die Blase des Browsers — die beim
        nächsten Klick verschwindet und auf manchem Telefon gar nicht erscheint.
      */
      setAttempted(true);
      const first = FIELD_ORDER.find((field) => clientErrors[field] !== undefined);
      if (first !== undefined) {
        setError(null);
        document.getElementById(fieldId(first))?.focus();
        return;
      }
    }

    setBusy(true);
    setError(null);

    try {
      if (!isRegister) {
        await login({ email, password, rememberMe });
      } else if (sent === null) {
        setSent(
          await startRegistration({
            email,
            password,
            firstName,
            lastName,
            locale: "de-DE",
            acceptedTermsVersion: TERMS_VERSION,
          }),
        );
        setCode("");
      } else {
        await confirmRegistration({ email, code, rememberMe });
      }
    } catch (cause: unknown) {
      /*
        Ein Verstoß gegen den Vertrag gehört an sein Feld und nicht in einen
        Kasten, der „Die Anfrage entspricht nicht dem Vertrag." sagt. Nur was
        sich keinem Feld zuordnen lässt, bleibt dort.
      */
      const onFields =
        isRegister && sent === null && cause instanceof RequestError
          ? fieldErrorsFromDetails(cause.details, { firstName, lastName, email, password })
          : {};
      if (Object.keys(onFields).length > 0) {
        setServerErrors(onFields);
        const first = FIELD_ORDER.find((field) => onFields[field] !== undefined);
        if (first !== undefined) document.getElementById(fieldId(first))?.focus();
        return;
      }
      // Die Meldung kommt vom Server und ist bereits auf Deutsch und ohne
      // Innenleben formuliert — siehe CONVENTIONS.md, Abschnitt Fehler.
      setError(cause instanceof Error ? cause.message : "Unbekannter Fehler.");
    } finally {
      setBusy(false);
    }
  }

  async function resend(): Promise<void> {
    setBusy(true);
    setError(null);

    try {
      setSent(await resendRegistrationCode(email));
      setCode("");
    } catch (cause: unknown) {
      setError(cause instanceof Error ? cause.message : "Unbekannter Fehler.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout
      headline={
        atCodeStep
          ? "Nur du kannst dieses Konto bestätigen."
          : isRegister
            ? "Ein Konto, alle Anlagen, volle Historie."
            : "Verbrauch und Erzeugung, nachvollziehbar gemessen."
      }
      lead={
        atCodeStep
          ? "Der Code gilt zehn Minuten. Nach fünf falschen Eingaben beginnt der Vorgang von vorn."
          : isRegister
            ? "Deine erste Anlage entsteht zusammen mit dem Konto. Deine Geräte messen unabhängig davon weiter."
            : "Dein Gerät misst und speichert im Heimnetz, auch ohne Konto. Was in der Cloud liegt, siehst du hier — über alle Anlagen hinweg und über die Zeit."
      }
    >
      {atCodeStep && (
        <AuthBack
          onClick={() => {
            setSent(null);
            setError(null);
          }}
        >
          Angaben ändern
        </AuthBack>
      )}

      {isRegister && <AuthSteps current={atCodeStep ? 2 : 1} total={2} />}

      <AuthHeading
        title={atCodeStep ? "Code eingeben" : isRegister ? "Konto anlegen" : "Willkommen zurück"}
        lead={
          atCodeStep
            ? `Wir haben ihn an ${email} geschickt.`
            : isRegister
              ? "Wir schicken dir einen Code per E-Mail. Erst danach entsteht das Konto."
              : "Melde dich mit deinem Konto an."
        }
      />

      <form
        style={{ display: "flex", flexDirection: "column", gap: "var(--sp-5)" }}
        // Beim Registrieren prüft das Formular selbst und sagt es am Feld; die
        // Blase des Browsers daneben wäre eine zweite, flüchtigere Auskunft.
        noValidate={isRegister}
        onSubmit={(event) => void submit(event)}
      >
        {atCodeStep ? (
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--sp-3)" }}>
            <CodeField
              label="Sicherheitscode"
              length={sent.codeLength}
              value={code}
              onChange={setCode}
              disabled={busy}
              autoFocus
            />
            <Countdown until={sent.expiresAt} />
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--sp-4)" }}>
            {isRegister && (
              /*
                Zwei Felder nebeneinander, solange Platz ist — `flex-wrap` und
                keine Medienabfrage, wie im Rahmen darum: Maßgeblich ist die
                Breite des Formulars und nicht die des Fensters.
              */
              <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--sp-4)" }}>
                <Input
                  id={fieldId("firstName")}
                  label="Vorname"
                  type="text"
                  size="lg"
                  style={{ flex: "1 1 140px" }}
                  value={firstName}
                  autoComplete="given-name"
                  required
                  error={errorFor("firstName")}
                  onChange={(event) => {
                    setFirstName(event.target.value);
                    edited("firstName");
                  }}
                />
                <Input
                  id={fieldId("lastName")}
                  label="Nachname"
                  type="text"
                  size="lg"
                  style={{ flex: "1 1 140px" }}
                  value={lastName}
                  autoComplete="family-name"
                  required
                  error={errorFor("lastName")}
                  onChange={(event) => {
                    setLastName(event.target.value);
                    edited("lastName");
                  }}
                />
              </div>
            )}

            <Input
              id={fieldId("email")}
              label="E-Mail"
              type="email"
              size="lg"
              value={email}
              autoComplete="email"
              required
              error={errorFor("email")}
              onChange={(event) => {
                setEmail(event.target.value);
                edited("email");
              }}
            />

            <PasswordField
              id={fieldId("password")}
              label="Passwort"
              size="lg"
              value={password}
              autoComplete={isRegister ? "new-password" : "current-password"}
              // Dieselbe Zusage wie im Vertrag (PasswordSchema), und beim Tippen
              // schon, wie viel fehlt — grau, bis abgeschickt wurde.
              hint={isRegister ? passwordHint(password) : undefined}
              error={errorFor("password")}
              required
              onChange={(event) => {
                setPassword(event.target.value);
                edited("password");
              }}
            />

            <div>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "var(--sp-3)",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Checkbox
                  checked={rememberMe}
                  onChange={setRememberMe}
                  label="Angemeldet bleiben"
                />
                {/*
                  **Nur beim Anmelden.** Wer gerade ein Konto anlegt, hat noch kein
                  Passwort, das er vergessen haben könnte — der Link wäre dort ein
                  Angebot ins Leere.
                */}
                {!isRegister && (
                  <AuthLink to={{ kind: "forgot-password" }}>Passwort vergessen?</AuthLink>
                )}
              </div>
              {/*
                Der Abstand ist nötig, nicht kosmetisch: Bricht die Zeile darüber
                um — auf einem Telefon der Normalfall —, rutscht „Passwort
                vergessen?" unter die Checkbox, und ohne ihn läse sich der graue
                Satz wie die Erklärung zum Link.

                **Beim Registrieren steht er nicht.** Dort geht es darum, ein
                Konto zu bekommen; wie lange die Sitzung danach läuft, ist die
                Frage des Augenblicks, in dem man sich anmeldet — und der Haken
                daneben sagt bereits, was er tut. Ein Satz, der an dieser Stelle
                eine Sitzung beschreibt, die es noch gar nicht gibt, ist ein Satz
                zu viel.
              */}
              {!isRegister && (
                <p style={{ margin: "var(--sp-2) 0 0", fontSize: 12, color: "var(--text-faint)" }}>
                  {rememberMe
                    ? `${SESSION_REMEMBER_DAYS} Tage, auch nach dem Schließen des Browsers.`
                    : `${SESSION_HOURS} Stunden oder bis du den Browser schließt.`}
                </p>
              )}
            </div>

            {isRegister && (
              /*
                **Und jetzt mit Links** (HM-061, OQ-033 damit entschieden). Bis
                hierher stand der Satz ohne sie, weil es die Dokumente als Seiten
                dieser Anwendung nicht gab — ein `href="#"` hätte ausgesehen wie
                das Angebot, vor der Zustimmung zu lesen, und wäre keines
                gewesen. Es gibt sie jetzt, und damit ist der Haken das, was er
                sein soll: eine Zustimmung zu einem Text, den man vorher
                aufmachen kann.

                Die Fassung, die als `acceptedTermsVersion` festgehalten wird,
                und die Fassung auf der verlinkten Seite kommen aus **derselben**
                Konstante (`lib/legal.ts`). Zwei Zahlen dafür wären genau der
                Mangel, den OQ-033 beschrieben hat, nur eine Ebene höher.
              */
              <Checkbox
                id={fieldId("terms")}
                checked={termsAccepted}
                onChange={setTermsAccepted}
                error={errorFor("terms")}
                label={
                  <span>
                    Ich stimme den{" "}
                    <AuthLink to={{ kind: "legal", document: "terms" }}>
                      Nutzungsbedingungen
                    </AuthLink>{" "}
                    und der{" "}
                    <AuthLink to={{ kind: "legal", document: "privacy" }}>
                      Datenschutzerklärung
                    </AuthLink>{" "}
                    zu.
                  </span>
                }
              />
            )}
          </div>
        )}

        {error !== null && <Alert>{error}</Alert>}

        {/*
          **Der fehlende Haken sperrt den Knopf nicht mehr.** Ein gesperrter
          Knopf sagt nicht, woran es liegt; ein Klick darauf sagt es jetzt am
          Haken selbst. Beim Code bleibt die Sperre: Dort ist sichtbar, was fehlt,
          und jeder abgeschickte Versuch zählt.
        */}
        <Button
          type="submit"
          size="lg"
          block
          disabled={busy || (atCodeStep && code.length !== sent.codeLength)}
        >
          {busy
            ? "Einen Moment …"
            : atCodeStep
              ? "Bestätigen"
              : isRegister
                ? "Code anfordern"
                : "Anmelden"}
        </Button>
      </form>

      {atCodeStep ? (
        <>
          <AuthNote>
            HausMeter fragt nie nach diesem Code — weder am Telefon noch im Chat. Gibt es zu dieser
            Adresse schon ein Konto, haben wir stattdessen einen Hinweis zum Anmelden geschickt.
          </AuthNote>
          <ResendLine
            availableAt={sent.resendAvailableAt}
            disabled={busy}
            onResend={() => void resend()}
          />
        </>
      ) : (
        !isRegister && (
          <AuthNote>
            Dein Gerät im Heimnetz misst und speichert auch ohne Konto. Diese Ansicht zeigt, was in
            der Cloud liegt, und meldet dafür immer an einem Konto an.
          </AuthNote>
        )
      )}

      {!atCodeStep && (
        <p style={{ margin: 0, fontSize: 13.5, color: "var(--text-muted)" }}>
          {isRegister ? "Bereits registriert? " : "Noch kein Konto? "}
          <AuthSwitch
            onClick={() => {
              setMode(isRegister ? "login" : "register");
              setSent(null);
              setError(null);
              setAttempted(false);
              setServerErrors({});
            }}
          >
            {isRegister ? "Anmelden" : "Registrieren"}
          </AuthSwitch>
        </p>
      )}
    </AuthLayout>
  );
}

/** In dieser Reihenfolge stehen die Felder da — der Fokus geht zum ersten Fehler. */
const FIELD_ORDER: readonly RegistrationField[] = [
  "firstName",
  "lastName",
  "email",
  "password",
  "terms",
];

/** Der graue Hinweis am Passwort, solange noch nicht abgeschickt wurde. */
function passwordHint(password: string): string {
  const missing = PASSWORD_MIN_LENGTH - password.length;
  return password.length > 0 && missing > 0
    ? `Noch ${missing} Zeichen — mindestens ${PASSWORD_MIN_LENGTH}.`
    : `Mindestens ${PASSWORD_MIN_LENGTH} Zeichen.`;
}

/**
 * Wie lange der Code noch gilt.
 *
 * **Läuft er ab, wird das gesagt und nicht bloß auf 00:00 gezählt.** Der Knopf
 * bleibt trotzdem bedienbar: Ob der Vorgang wirklich zu Ende ist, entscheidet
 * der Server — die Uhr des Browsers kann falsch gehen, und ein gesperrter Knopf
 * an einem gültigen Code wäre die ärgerlichere Variante des Irrtums.
 */
function Countdown({ until }: { readonly until: string }) {
  const remaining = useCountdown(until);

  return (
    <p style={{ margin: 0, fontSize: 13, color: "var(--text-muted)" }}>
      {remaining > 0 ? (
        <>
          Noch{" "}
          <span
            style={{ fontFamily: "var(--font-mono)", fontWeight: 700, color: "var(--text-strong)" }}
          >
            {formatDuration(remaining)}
          </span>{" "}
          gültig
        </>
      ) : (
        "Der Code ist abgelaufen — fordere einen neuen an."
      )}
    </p>
  );
}

/**
 * „Keine E-Mail erhalten? Erneut senden."
 *
 * **Die Wartezeit sperrt jetzt wirklich** (HM-069). Bis dahin lief sie
 * daneben ab, während der Knopf die ganze Zeit bedienbar blieb — eine
 * angezeigte Sperre, die es nirgends gab. Der Einwand dagegen stimmte für den
 * *Server* („keine Sperre, begrenzt wird vom Kontingent") und war für die
 * *Oberfläche* die falsche Folgerung: `EMAIL_CODE_RESEND_COOLDOWN_SECONDS` sagt
 * selbst, dass sie der Oberfläche gehört und sagt, wann der Knopf wieder etwas
 * tut.
 *
 * Denn der frühe Klick ist nicht folgenlos. Er **entwertet den Code, der schon
 * im Postfach liegt** — wer die erste Nachricht öffnet und ihre Ziffern
 * eintippt, bekommt „Code ungültig" für einen Code, der eben noch richtig war.
 * Und er verbraucht **eines von drei Kontingenten je Viertelstunde**: Dreimal
 * ungeduldig geklickt heißt eine Viertelstunde ohne jeden neuen Code, während
 * daneben stand, man möge 47 Sekunden warten.
 *
 * Die Frist läuft dabei auf der **eigenen** Uhr (`resendDeadline`), damit eine
 * falsch gehende den Knopf nicht viel länger sperrt als die Wartezeit lang ist.
 */
function ResendLine({
  availableAt,
  disabled,
  onResend,
}: {
  readonly availableAt: string;
  readonly disabled: boolean;
  readonly onResend: () => void;
}) {
  /*
    Einmal je Antwort des Servers gerechnet und dann festgehalten: Ab dem
    Augenblick, in dem sie eintraf, läuft die Frist auf unserer Uhr. Ein neuer
    `availableAt` — also ein neuer Versand — beginnt sie von vorn.
  */
  const deadline = useMemo(() => resendDeadline(availableAt, Date.now()), [availableAt]);
  const remaining = useCountdown(deadline);
  const waiting = remaining > 0;

  return (
    <p style={{ margin: 0, fontSize: 13.5, color: "var(--text-muted)" }}>
      Keine E-Mail?{" "}
      <AuthSwitch onClick={onResend} disabled={disabled || waiting}>
        Erneut senden
      </AuthSwitch>
      {/*
        Die Wartezeit steht in Klammern und nicht als eigener Halbsatz: Sie ist
        eine Randbedingung des Links daneben, kein zweiter Hinweis. Dass gerade
        gesendet wird, zeigt der Knopf über dieser Zeile — ein „einen Moment …"
        auch hier wäre dieselbe Auskunft zweimal.
      */}
      {waiting && !disabled && (
        <span style={{ color: "var(--text-faint)" }}> (in {Math.ceil(remaining / 1000)} s)</span>
      )}
    </p>
  );
}

/**
 * Millisekunden bis zu einem Zeitpunkt, sekündlich nachgeführt.
 *
 * Der Timer läuft nur, solange etwas übrig ist: Ein Intervall, das eine
 * abgelaufene Frist weiterzählt, hält die Seite ohne Zweck wach.
 *
 * **Zwei Arten von Ziel, und der Unterschied ist der Punkt.** Der Ablauf des
 * Codes ist ein Zeitpunkt des **Servers** (`string`) — er wird angezeigt und
 * sperrt nichts, denn ob der Vorgang zu Ende ist, entscheidet der Server. Die
 * Wartezeit vor dem nächsten Versand ist ein Zeitpunkt auf der **eigenen** Uhr
 * (`number`, aus `resendDeadline`), weil an ihr ein Knopf hängt: Eine Sperre,
 * die auf einer fremden Uhr rechnet, ist so falsch wie die Uhr des Browsers.
 */
function useCountdown(target: string | number): number {
  const [remaining, setRemaining] = useState(() => msUntil(target));

  useEffect(() => {
    setRemaining(msUntil(target));
    if (msUntil(target) <= 0) return;

    const timer = setInterval(() => {
      const left = msUntil(target);
      setRemaining(left);
      if (left <= 0) clearInterval(timer);
    }, 1000);

    return () => clearInterval(timer);
  }, [target]);

  return remaining;
}

function msUntil(target: string | number): number {
  const at = typeof target === "number" ? target : new Date(target).getTime();
  return Math.max(0, at - Date.now());
}

/** `mm:ss`, wie im Entwurf. */
function formatDuration(ms: number): string {
  const total = Math.ceil(ms / 1000);
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}
